import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { clearLogin } from '../../sdk/store/authActions';
import { getAmwayLogoutUrl, getEnvironment } from '../../ui/utils';
import { useAppDispatch } from '../../hooks';
import styles from './Admin.module.scss';

interface AdminMenuProps {
  adminType: number;
}

interface RenderTree {
  id: string;
  name: string;
  url?: string;
  newTab?: boolean;
  children?: readonly RenderTree[];
}

export const AdminMenu = ({ adminType }: AdminMenuProps ) => {
  let location = useLocation();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const navigate = useNavigate();

  const isLocal = useRef(['LOCAL', 'ISSDEV', 'ISSUAT'].includes(getEnvironment())).current;

  const [menuExpanded, setMenuExpanded] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    let rootEl = document.querySelector('#root');
    if (rootEl) rootEl.scrollTo(0, 0);

    const path = location.pathname;
    let _selectedMenu = flattedMenuList.find(menu => menu.url === path);
    if (_selectedMenu) {
      setSelected(_selectedMenu.id);
      setExpanded(_selectedMenu.ids);
    }
  }, [location]);

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };

  const handleSelect = (event: React.SyntheticEvent, nodeIds: string) => {
    setSelected(nodeIds);
  };

  const handleMenuClick = () => {
    sessionStorage.removeItem('FilterOptions');
    sessionStorage.removeItem('Pagination');
  };


    const masterMaintenance: RenderTree = {
      id: 'master-maintenance',
      name: 'Master Maintenance',
      children: [
        { id: 'user-setup', name: 'User Master', url: '/admin/master/user-setup/list' },
        { id: 'reward-master', name: 'Reward Master', url: '/admin/master/reward-setup/list' },
        { id: 'location-master', name: 'Location Master', url: '/admin/master/location-setup/list' },
        { id: 'campaign-configuration', name: 'Campaign Configuration', url: '/admin/master/campaign-configuration/list' },
      ],
    };

    // challenge menu with dynamic children
    const challenge: RenderTree = {
      id: 'challenge',
      name: 'Challenge',
      children: (() => {
        // different children depending on adminType
        if (adminType === 1 || adminType === 2) {
          // SUPERADMIN sees all children
          return [
            { id: 'manage-participant', name: 'Manage Participant', url: '/admin/challenge/manage-participant/list' },
            { id: 'manage-team', name: 'Manage Team', url: '/admin/challenge/manage-team/list' },
            { id: 'manage-coach', name: 'Manage Team Leader', url: '/admin/challenge/manage-coach/list' },
            {
              id: 'challenge-report',
              name: 'Report',
              children: [
                { id: 'team', name: 'Participants', url: '/admin/challenge/report/team' },
                { id: 'coach-group', name: 'Team Leader (Group)', url: '/admin/challenge/report/team-leader-group' },
              ],
            },
          ];
        } 
        return []; // others see nothing under challenge
      })(),
    };

    // promotion menu
    const promotion: RenderTree = {
      id: 'promotion',
      name: 'Promotion',
      children: (() => {
        // different children depending on adminType
        if (adminType === 1) {
          // SUPERADMIN sees all children
          return [
            { id: 'manage-inventory', name: 'Manage Inventory', url: '/admin/promotion/manage-inventory/list' },
            { id: 'manage-redemption', name: 'Manage Redemption', url: '/admin/promotion/manage-redemption/list' },
            {
              id: 'promo-report',
              name: 'Report',
              children: [
                { id: 'inventory', name: 'Inventory', url: '/admin/promotion/report/inventory' },
                { id: 'redemption', name: 'Redemption', url: '/admin/promotion/report/redemption' },
              ],
            },
            {
              id: 'promo-bulk-upload',
              name: 'Bulk Upload',
              children: [
                { id: 'inventory', name: 'Inventory', url: '/admin/promotion/bulk-upload/inventory' },
              ],
            },
          ];
        } else if (adminType === 3) {
          // PROMOTION admin sees only participant + report
          return [
            { id: 'manage-inventory', name: 'Manage Inventory', url: '/admin/promotion/manage-inventory/list' },
            { id: 'manage-redemption', name: 'Manage Redemption', url: '/admin/promotion/manage-redemption/list' },
          ];
        }
        return []; // others see nothing under challenge
      })(),
    };

    // decide which top-level menus to push
    const menus: RenderTree[] = [];
    switch (adminType) {
      case 1: // SUPERADMIN
        menus.push(masterMaintenance, challenge, promotion);
        break;
      case 2: // CHALLENGE
        menus.push(challenge);
        break;
      case 3: // PROMOTION
        menus.push(promotion);
        break;
      default:
        break;
    }

  const flattedMenuList = useMemo(() => {
    let arr: (RenderTree & { ids: string[] })[] = [];
    const checkChildren = (items: readonly RenderTree[] | null, ids: string[] = []) => {
      if (!items) items = menus;
      items.forEach(item => {
        let _ids = [...ids, item.id];
        if (item.children) checkChildren(item.children, _ids);
        if (item.url) arr.push({ ...item, ids: _ids });
      });
    };
    checkChildren(null);
    return arr;
  }, [menus]);

  const renderTree = useCallback(
    (nodes: RenderTree) => {
      if (!nodes.children && nodes.url) {
        return (
          <li
            className={`${styles['tree-item-list']} ${
              selected === nodes.id ? styles['tree-item-list-selected'] : ''
            }`.trim()}
          >
            <Link key={nodes.id} to={nodes.url} target={nodes.newTab ? '_blank' : '_self'} onClick={handleMenuClick}>
              {nodes.name}
            </Link>
          </li>
        );
      } else {
        return (
          <TreeItem key={nodes.id} nodeId={nodes.id} label={nodes.name} className={styles['tree-item']}>
            {Array.isArray(nodes.children) ? nodes.children.map(node => renderTree(node)) : null}
          </TreeItem>
        );
      }
    },
    [selected],
  );

  const logoutFn = (): Function => {
    const localSignIn = () => {
      setTimeout(() => {
        navigate('/');
      }, 0);
    };

    const entraSignOut = async () => {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    };

    const amwayIdSignOut = () => {
      window.location.href = getAmwayLogoutUrl();
    };

    if (isLocal) {
      return localSignIn;
    }

    return async () => {
      await entraSignOut();
      amwayIdSignOut();
    };
  };

  const doLogout = () => {
    localStorage.removeItem('pinNumber');
    localStorage.removeItem('appToken');
    localStorage.removeItem('loginToken');
    localStorage.removeItem('exp');
    localStorage.removeItem('userId');
    localStorage.removeItem('loginId');
    dispatch(clearLogin(logoutFn()));
    navigate('/');
  };

  const handleMenuExpandClick = (val: boolean) => {
    setMenuExpanded(val);
  };
  return (
    <div className={`${styles['portalMenus']} ${menuExpanded ? '' : styles['hide']}`}>
      {menuExpanded && (
        <div
          className={`${styles['button']} ${styles['collapse']}`}
          onClick={() => handleMenuExpandClick(false)}
          title="Collapse Menu"
        >
          &laquo;
        </div>
      )}
      {!menuExpanded && (
        <div
          className={`${styles['button']} ${styles['expand']}`}
          onClick={() => handleMenuExpandClick(true)}
          title="Expand Menu"
        >
          &raquo;
        </div>
      )}
      <TreeView
        aria-label="rich object"
        className="www"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        selected={selected}
        onNodeToggle={handleToggle}
        onNodeSelect={handleSelect}
        sx={{ height: 110, flexGrow: 1, maxWidth: 400, overflowY: 'auto' }}
      >
        {menus.map(m => renderTree(m))}
        <div className={styles['logout-container']}>
          <button className={styles['logout-button']} onClick={doLogout}>
            Logout
          </button>
        </div>
      </TreeView>
    </div>
  );
};
